import { ApolloClient, ApolloLink, from, InMemoryCache, Observable } from "@apollo/client";
import { createAuthErrorLink } from "@shared/feShared/auth/tokenExchange";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { authClient, clearBootstrapPromise, getBootstrapPromise } from "@/auth/authClientInstance";

const uploadLink = createUploadLink({
  uri: (operation) => {
    const base = `${import.meta.env.VITE_API_DOMAIN}/graphql`;
    return operation.operationName ? `${base}/${operation.operationName}` : base;
  },
  credentials: "include",
});

/**
 * Gates all outbound operations on the Cognito bootstrap refresh kicked off
 * in `authClientInstance.ts`. While that promise is pending, queries are
 * held; once it resolves (success or failure), the link clears the gate and
 * normal forwarding resumes — so this only adds latency to requests that
 * would otherwise have raced the very first refresh on page load.
 */
export const bootstrapLink = new ApolloLink((operation, forward) => {
  const pending = getBootstrapPromise();
  if (!pending) {
    return forward(operation);
  }
  return new Observable((observer) => {
    let subscription: ReturnType<ReturnType<typeof forward>["subscribe"]> | null = null;
    let unsubscribed = false;
    const proceed = () => {
      clearBootstrapPromise();
      if (unsubscribed) return;
      subscription = forward(operation).subscribe(observer);
    };
    pending.then(proceed, proceed);
    return () => {
      unsubscribed = true;
      subscription?.unsubscribe();
    };
  });
});

const authLink = new ApolloLink((operation, forward) => {
  const token = authClient.getAccessToken();
  operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  }));
  return forward(operation);
});

const errorLink = createAuthErrorLink({
  authClient,
  onGraphQLErrors: (errors) => {
    errors.forEach(({ message }) => {
      console.log(`[GraphQL error]: Message: ${message}`);
    });
  },
  onNetworkError: (error) => {
    console.log(`[Network error]: ${error}`);
  },
});

export const apolloClient = new ApolloClient({
  link: from([bootstrapLink, errorLink, authLink, uploadLink] as ApolloLink[]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          supplyChainBooking: {
            merge: true,
          },
        },
      },
      User: {
        keyFields: (object) => {
          // If user ID is "0" (indicating a temporary/unconfirmed user),
          // use email as part of the cache key to prevent merging
          if (object.id === "0") {
            return `User:${object.id}:${object.email}`;
          }
          // For regular users, use the default ID-based key
          return `User:${object.id}`;
        },
      },
    },
  }),
});
